import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import './UploadSyllabusCard.css';

const ACCEPT =
  'application/pdf,.pdf,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx';

const PICKER_TYPES = [
  {
    description: 'Documents',
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    }
  }
];

const UPLOAD_DURATION_MS = 3500;

function pickFile(list) {
  if (!list || list.length === 0) return null;
  const f = list[0];
  const name = (f.name || '').toLowerCase();
  const ok = name.endsWith('.pdf') || name.endsWith('.doc') || name.endsWith('.docx');
  return ok ? f : null;
}

/**
 * Upload syllabus — ai-sales-bot layout, but:
 * - File `<input>` is portaled to `document.body` so it is not under Pearson `innerHTML` (avoids dialog hangs).
 * - Chromium: prefer `showOpenFilePicker` (different path than legacy file dialog).
 */
export default function UploadSyllabusCard({ hasExams = false }) {
  const fileInputId = useId();
  const inputRef = useRef(null);
  const uploadRafRef = useRef(null);
  const mountedRef = useRef(true);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [okMessage, setOkMessage] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (uploadRafRef.current != null) {
        window.cancelAnimationFrame(uploadRafRef.current);
        uploadRafRef.current = null;
      }
    };
  }, []);

  const assignFile = useCallback((next) => {
    setError(null);
    setOkMessage(null);
    setFile(next);
  }, []);

  const onInputChange = (e) => {
    const next = pickFile(e.target.files);
    assignFile(next);
    if (e.target.files?.length && !next) {
      setError('Please choose a PDF or Word (.doc / .docx) file.');
    }
    e.target.value = '';
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = pickFile(e.dataTransfer.files);
    assignFile(next);
    if (e.dataTransfer.files?.length && !next) {
      setError('Please drop a PDF or Word (.doc / .docx) file.');
    }
  };

  const openWithFilePicker = useCallback(() => {
    (async () => {
      try {
        const [handle] = await window.showOpenFilePicker({
          multiple: false,
          types: PICKER_TYPES
        });
        const raw = await handle.getFile();
        const next = pickFile([raw]);
        assignFile(next);
        if (raw && !next) {
          setError('Please choose a PDF or Word (.doc / .docx) file.');
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        inputRef.current?.click();
      }
    })();
  }, [assignFile]);

  const onLabelClick = useCallback(
    (e) => {
      if (typeof window.showOpenFilePicker !== 'function') return;
      e.preventDefault();
      e.stopPropagation();
      openWithFilePicker();
    },
    [openWithFilePicker]
  );

  const onLabelKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (typeof window.showOpenFilePicker !== 'function') return;
      e.preventDefault();
      e.stopPropagation();
      openWithFilePicker();
    },
    [openWithFilePicker]
  );

  const onUpload = useCallback(() => {
    if (!file || busy) return;
    if (uploadRafRef.current != null) {
      window.cancelAnimationFrame(uploadRafRef.current);
      uploadRafRef.current = null;
    }
    setBusy(true);
    setError(null);
    setOkMessage(null);
    setUploadProgress(0);

    const start = performance.now();

    const step = (now) => {
      if (!mountedRef.current) return;
      const elapsed = now - start;
      const t = Math.min(1, elapsed / UPLOAD_DURATION_MS);
      setUploadProgress(t * 100);
      if (t < 1) {
        uploadRafRef.current = window.requestAnimationFrame(step);
      } else {
        uploadRafRef.current = null;
        setUploadProgress(100);
        setOkMessage('File uploaded successfully.');
        setBusy(false);
      }
    };

    uploadRafRef.current = window.requestAnimationFrame(step);
  }, [file, busy]);

  const fileInput = (
    <input
      ref={inputRef}
      id={fileInputId}
      accept={ACCEPT}
      type="file"
      className="upload-syllabus-card__fileInput upload-syllabus-card__fileInput--portaled"
      onChange={onInputChange}
      aria-hidden="true"
      tabIndex={-1}
    />
  );

  return (
    <>
      {createPortal(fileInput, document.body)}

      <div
        data-componentid="UPLOAD_SYLLABUS_CARD"
        data-hasexams={hasExams ? 'true' : 'false'}
        className="upload-syllabus-card"
      >
        <div className="upload-syllabus-card__inner">
          <div className="upload-syllabus-card__header">
            <h2 className="upload-syllabus-card__title">Upload syllabus</h2>
          </div>
          <div className="upload-syllabus-card__body">
            <div className="upload-syllabus-card__modal">
              <div className="upload-syllabus-card__content">
                <div
                  className="upload-syllabus-card__dropZone"
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  role="presentation"
                >
                  {/* Input is portaled to document.body; htmlFor still targets it in the document. */}
                  {/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/no-noninteractive-element-interactions */}
                  <label
                    className="upload-syllabus-card__dropButton"
                    htmlFor={fileInputId}
                    onClick={onLabelClick}
                    onKeyDown={onLabelKeyDown}
                  >
                    <div className="upload-syllabus-card__dropInner">
                      <p className="upload-syllabus-card__dropText">
                        Drag your files here or click to upload
                        <span className="upload-syllabus-card__iconWrap" aria-hidden="true">
                          <svg
                            className="upload-syllabus-card__icon"
                            focusable="false"
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d={
                                'M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96' +
                                'M14 13v4h-4v-4H7l5-5 5 5z'
                              }
                            />
                          </svg>
                        </span>
                      </p>
                    </div>
                  </label>
                  {/* eslint-enable jsx-a11y/label-has-associated-control, jsx-a11y/no-noninteractive-element-interactions */}
                </div>
                {file ? (
                  <p className="upload-syllabus-card__fileName" title={file.name}>
                    Selected: {file.name}
                  </p>
                ) : null}
                {error ? (
                  <p className="upload-syllabus-card__status upload-syllabus-card__status--error">{error}</p>
                ) : null}
                {okMessage ? (
                  <p className="upload-syllabus-card__status upload-syllabus-card__status--ok">{okMessage}</p>
                ) : null}
                <button
                  type="button"
                  disabled={!file || busy}
                  className={
                    busy
                      ? 'upload-syllabus-card__uploadBtn upload-syllabus-card__uploadBtn--busy'
                      : 'upload-syllabus-card__uploadBtn'
                  }
                  aria-busy={busy}
                  style={{ alignSelf: 'end' }}
                  onClick={onUpload}
                >
                  {busy ? (
                    <>
                      <span
                        className="upload-syllabus-card__uploadBtnProgress"
                        style={{ width: `${uploadProgress}%` }}
                        aria-hidden
                      />
                      <span className="upload-syllabus-card__uploadBtnLabel">
                        Uploading… {Math.round(uploadProgress)}%
                      </span>
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

UploadSyllabusCard.propTypes = {
  hasExams: PropTypes.bool
};

UploadSyllabusCard.defaultProps = {
  hasExams: false
};
